import React, { useEffect, useState, useCallback } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Stack,
    IconButton,
    Chip,
    Avatar,
    FormControlLabel,
    Checkbox,
    TextField,
    Autocomplete,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    LinearProgress,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useForm, useFieldArray } from "react-hook-form";
import { request } from "../../../../../utils/request";
import { toast } from "react-toastify";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import Brandcrumb from "../../../../../componentsUiBlock/Brandcrumb";
import { Brand, CarFormData } from "../../../types";
import { useImageCompression } from "../../../../../utils/useImageCompression";

const FUEL_OPTIONS = ["Petrol", "Diesel", "CNG", "Electric", "Hybrid"];
const TRANSMISSION_OPTIONS = ["Manual", "Automatic", "CVT", "AMT"];

// Placeholder for the initial shadow car image
const ShadowCarPlaceholder = (
    <Box
        sx={{
            width: "100%",
            height: 300,
            bgcolor: '#f0f0f0',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mt: 1,
            boxShadow: '0 8px 16px rgba(0,0,0,0.1), 0 0 10px rgba(0,0,0,0.05) inset',
            color: 'text.secondary',
            flexDirection: 'column',
            overflow: 'hidden',
            backgroundImage: 'url("https://pictures.dealer.com/m/mannchevroletbuickgmc/1459/a976281f56ff06d46dc390283aa14da6x.jpg?impolicy=downsize_bkpt&imdensity=1&w=520")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
        }}
    >
        <Typography
            variant="caption"
            color="text.disabled"
            sx={{
                mt: 0,
                backgroundColor: 'rgba(255,255,255,0.9)',
                px: 1,
                borderRadius: 1
            }}
        >
            Select base image from generations
        </Typography>
    </Box>
);

const AddCar: React.FC = () => {
    const { compressMultipleImages, isCompressing } = useImageCompression();
    const navigate = useNavigate();
    const [brands, setBrands] = useState<Brand[]>([]);
    const [variantInput, setVariantInput] = useState('');
    const [compressingGenerations, setCompressingGenerations] = useState<{ [key: number]: boolean }>({});

    const {
        register,
        handleSubmit,
        reset,
        control,
        setValue,
        watch,
        formState: { errors },
    } = useForm<CarFormData>({
        defaultValues: {
            name: "",
            baseImage: null,
            brand: { name: "", logo: "", parentCompany: "" },
            variants: [],
            fuelTypes: [],
            transmissions: [],
            generations: [{ from: "", to: "", current: false, images: [] }],
        },
    });

    const { append: appendVariant, remove: removeVariant } =
        useFieldArray({ control, name: "variants" });

    const { fields: genFields, append: appendGen, remove: removeGen } =
        useFieldArray({ control, name: "generations" });

    const fuelType = watch("fuelTypes");
    const transmission = watch("transmissions");
    const baseImage = watch("baseImage");
    const watchedBrand = watch("brand");
    const watchedVariants = watch("variants");
    const generations = watch("generations");

    // Get base image preview URL
    const getBaseImagePreview = () => {
        if (!baseImage) return null;

        const { generationIndex, imageIndex } = baseImage;
        const generation = generations[generationIndex];

        if (generation && generation.images && generation.images[imageIndex]) {
            const img = generation.images[imageIndex];
            return img instanceof File ? URL.createObjectURL(img) : img;
        }

        return null;
    };

    const baseImagePreview = getBaseImagePreview();

    // Auto-select first generation's first image as base image when images are uploaded
    useEffect(() => {
        const firstGeneration = generations[0];
        if (firstGeneration?.images?.length > 0 && !baseImage) {
            setValue("baseImage", { generationIndex: 0, imageIndex: 0 });
        }
    }, [generations, baseImage, setValue]);

    // Variant Chip Logic
    const handleAddVariant = useCallback(() => {
        const trimmedValue = variantInput.trim();
        const isDuplicate = watchedVariants.some(v => v.value.toLowerCase() === trimmedValue.toLowerCase());

        if (trimmedValue && !isDuplicate) {
            appendVariant({ value: trimmedValue });
            setVariantInput('');
        } else if (isDuplicate) {
            toast.warn(`Variant "${trimmedValue}" is already added.`);
            setVariantInput('');
        }
    }, [variantInput, watchedVariants, appendVariant]);

    const handleRemoveVariant = useCallback((index: number) => {
        removeVariant(index);
    }, [removeVariant]);

    const handleFuelClick = (fuel: string) => {
        const updated = fuelType.includes(fuel)
            ? fuelType.filter((f) => f !== fuel)
            : [...fuelType, fuel];
        setValue("fuelTypes", updated, { shouldValidate: true });
    };

    const handleTransmissionClick = (type: string) => {
        const updated = transmission.includes(type)
            ? transmission.filter((t) => t !== type)
            : [...transmission, type];
        setValue("transmissions", updated, { shouldValidate: true });
    };

    // Handle generation image upload with compression
    const handleGenerationImageUpload = async (genIndex: number, files: File[]) => {
        if (files.length === 0) return;

        // Validate file sizes
        const invalidFiles = files.filter(f => f.size > 10 * 1024 * 1024);
        if (invalidFiles.length > 0) {
            toast.error('Some images exceed 10MB limit');
            return;
        }

        // Validate file types
        const invalidTypes = files.filter(f => !f.type.startsWith('image/'));
        if (invalidTypes.length > 0) {
            toast.error('Please upload only image files');
            return;
        }

        try {
            setCompressingGenerations(prev => ({ ...prev, [genIndex]: true }));
            
            // Compress all images
            const compressedFiles = await compressMultipleImages(files);
            
            // Get current images for this generation
            const currentImages = watch(`generations.${genIndex}.images`) || [];
            
            // Append compressed files
            setValue(`generations.${genIndex}.images`, [...currentImages, ...compressedFiles]);
            
            toast.success(`${compressedFiles.length} image(s) uploaded successfully`);
        } catch (error) {
            console.error('Compression error:', error);
            toast.error('Failed to compress images. Please try again.');
            
            // Fallback: add original files if compression fails
            const currentImages = watch(`generations.${genIndex}.images`) || [];
            setValue(`generations.${genIndex}.images`, [...currentImages, ...files]);
        } finally {
            setCompressingGenerations(prev => ({ ...prev, [genIndex]: false }));
        }
    };

    // Handle remove generation image
    const handleRemoveGenerationImage = (genIndex: number, imgIndex: number) => {
        const currentImages = watch(`generations.${genIndex}.images`) || [];
        const updatedImages = currentImages.filter((_, i) => i !== imgIndex);
        setValue(`generations.${genIndex}.images`, updatedImages);

        // Update base image selection if the removed image was selected as base image
        if (baseImage && baseImage.generationIndex === genIndex && baseImage.imageIndex === imgIndex) {
            const newGenImages = updatedImages;
            if (newGenImages.length > 0) {
                setValue("baseImage", { generationIndex: genIndex, imageIndex: 0 });
            } else {
                // Find another generation with images
                const anotherGenWithImages = generations.findIndex((gen, idx) =>
                    idx !== genIndex && gen.images && gen.images.length > 0
                );
                if (anotherGenWithImages !== -1) {
                    setValue("baseImage", { generationIndex: anotherGenWithImages, imageIndex: 0 });
                } else {
                    setValue("baseImage", null);
                }
            }
        }
    };

    const onSubmit = async (data: CarFormData) => {
        try {
            // Validate that at least one generation has at least one image
            const hasGenerationImages = data.generations.some(gen =>
                gen.images && gen.images.length > 0
            );

            if (!hasGenerationImages) {
                toast.error("At least one generation must have at least one image");
                return;
            }

            // Validate base image selection
            if (!data.baseImage) {
                toast.error("Please select a base image from the generation images");
                return;
            }

            const formData = new FormData();
            formData.append("name", data.name);
            formData.append("brandName", data.brand.name || "");
            formData.append("brandLogo", data.brand.logo || "");
            formData.append("parentCompany", data.brand.parentCompany || "");

            // Append base image from generation images
            if (data.baseImage) {
                const { generationIndex, imageIndex } = data.baseImage;
                const baseImageFile = data.generations[generationIndex].images[imageIndex];
                if (baseImageFile instanceof File) {
                    formData.append("baseImage", baseImageFile);
                }
            }

            formData.append("variants", JSON.stringify(data.variants.map((v) => v.value).filter(v => v)));
            formData.append("fuelTypes", JSON.stringify(data.fuelTypes));
            formData.append("transmissions", JSON.stringify(data.transmissions));

            const generationsPayload = data.generations.map((g) => ({
                from: g.from,
                to: g.current ? "Present" : g.to,
                current: g.current,
                imagesCount: Array.isArray(g.images) ? g.images.length : 0,
            }));
            formData.append("generations", JSON.stringify(generationsPayload));

            data.generations.forEach((g) => {
                if (Array.isArray(g.images)) {
                    g.images.forEach((file) => {
                        if (file instanceof File) {
                            formData.append("generationImages", file);
                        }
                    });
                }
            });

            const res = await request({
                url: "compatibility/add-car-model",
                method: "post",
                data: formData,
                headers: { "Content-Type": "multipart/form-data" },
            });

            if (res.status === "success") {
                toast.success(res.message);
                reset();
                setVariantInput('');
                navigate("/configuration/cars");
            } else {
                toast.error(res.errors?.message);
            }
        } catch (error: any) {
            toast.error(error.message || "Failed to add car model");
        }
    };

    useEffect(() => {
        const fetchBrands = async () => {
            try {
                const res = await request({
                    url: "brand/get-all-brands",
                    method: "get",
                });
                if (res.status === "success") setBrands(res.brands);
            } catch (error: any) {
                console.error(error);
            }
        };
        fetchBrands();
    }, []);

    const carBrands = brands.filter(brand =>
        brand.manufactureType.includes('Cars')
    );

    const brandLogoDisplay = (
        <Avatar
            alt={watchedBrand.name}
            sx={{
                width: 60,
                height: 60,
                flexShrink: 0,
                bgcolor: 'white',
                border: '1px solid #ddd',
                '& img': {
                    objectFit: 'contain',
                    padding: '6px',
                }
            }}
        >
            <Box
                component="img"
                src={watchedBrand.logo}
                alt={`${watchedBrand.name} Logo`}
                sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
        </Avatar>
    );

    return (
        <>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
                <Brandcrumb />
            </Box>
            
            {/* Global compression indicator */}
            {isCompressing && (
                <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}>
                    <LinearProgress />
                    <Box sx={{ bgcolor: 'rgba(0,0,0,0.7)', color: 'white', p: 1, textAlign: 'center' }}>
                        <Typography variant="body2">Compressing images... Please wait</Typography>
                    </Box>
                </Box>
            )}
            
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Card elevation={4} sx={{ borderRadius: 3 }}>
                    <CardContent>
                        <Typography variant="h5" fontWeight={700} mb={3}>
                            Add New Car Model
                        </Typography>

                        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                            <Stack spacing={4}>

                                {/* 1. CAR DETAILS & SPECIFICATIONS */}
                                <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
                                    <Typography variant="h6" fontWeight={600} mb={3}>
                                        Car & Specifications
                                    </Typography>
                                    <Stack
                                        direction={{ xs: 'column', md: 'row' }}
                                        spacing={4}
                                    >
                                        {/* LEFT COLUMN: CAR DETAILS & BASE IMAGE */}
                                        <Box sx={{ width: { xs: '100%', md: '50%' } }}>
                                            <Stack spacing={2}>
                                                <TextField
                                                    size="small"
                                                    label="Car Model Name"
                                                    {...register("name", { required: "Car name is required" })}
                                                    error={!!errors.name}
                                                    helperText={errors.name?.message}
                                                    fullWidth
                                                />

                                                {/* Base Image Selection */}
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Select Base Image</InputLabel>
                                                    <Select
                                                        value={baseImage ? `${baseImage.generationIndex}-${baseImage.imageIndex}` : ""}
                                                        label="Select Base Image"
                                                        onChange={(e) => {
                                                            const [genIndex, imgIndex] = e.target.value.split('-').map(Number);
                                                            setValue("baseImage", { generationIndex: genIndex, imageIndex: imgIndex });
                                                        }}
                                                    >
                                                        {generations.map((gen, genIndex) =>
                                                            gen.images.map((img, imgIndex) => (
                                                                <MenuItem
                                                                    key={`${genIndex}-${imgIndex}`}
                                                                    value={`${genIndex}-${imgIndex}`}
                                                                >
                                                                    Generation {genIndex + 1} - Image {imgIndex + 1}
                                                                </MenuItem>
                                                            ))
                                                        )}
                                                    </Select>
                                                </FormControl>

                                                {/* Base Image Preview */}
                                                {baseImagePreview ? (
                                                    <Paper elevation={2} sx={{ borderRadius: 1, overflow: 'hidden', mt: 1 }}>
                                                        <Box
                                                            component="img"
                                                            src={baseImagePreview}
                                                            alt="Base Image Preview"
                                                            sx={{
                                                                width: "100%",
                                                                height: 300,
                                                                objectFit: "contain",
                                                                bgcolor: '#f5f5f5',
                                                            }}
                                                        />
                                                    </Paper>
                                                ) : (
                                                    ShadowCarPlaceholder
                                                )}
                                            </Stack>
                                        </Box>

                                        {/* RIGHT COLUMN: BRAND & SPECIFICATIONS */}
                                        <Box sx={{ width: { xs: '100%', md: '50%' } }}>
                                            <Stack spacing={3}>
                                                {/* Brand Selection */}
                                                <Box>
                                                    <Typography variant="subtitle1" fontWeight={500} mb={1}>
                                                        Brand Selection
                                                    </Typography>
                                                    <Autocomplete
                                                        size="small"
                                                        options={carBrands}
                                                        getOptionLabel={(option) => option.name}
                                                        onChange={(e, selectedBrand) => {
                                                            if (selectedBrand) {
                                                                setValue("brand.name", selectedBrand.name, { shouldValidate: true });
                                                                setValue("brand.logo", selectedBrand.brandLogo, { shouldValidate: true });
                                                                setValue("brand.parentCompany", selectedBrand.parentCompany, { shouldValidate: true });
                                                            } else {
                                                                setValue("brand", { name: "", logo: "", parentCompany: "" }, { shouldValidate: true });
                                                            }
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                size="small"
                                                                label="Select Brand"
                                                                {...register("brand.name", {
                                                                    required: "Brand name is required",
                                                                })}
                                                                error={!!errors.brand?.name}
                                                                helperText={errors.brand?.name?.message}
                                                            />
                                                        )}
                                                    />

                                                    {/* Brand Info Display */}
                                                    {watchedBrand.logo && (
                                                        <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 1, border: '1px solid #ddd', borderRadius: 1, mt: 1 }}>
                                                            {brandLogoDisplay}
                                                            <Box>
                                                                <Typography variant="body2" fontWeight={500}>
                                                                    {watchedBrand.parentCompany || 'N/A'}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Parent Company
                                                                </Typography>
                                                            </Box>
                                                        </Stack>
                                                    )}
                                                </Box>

                                                {/* Variants Section */}
                                                <Box>
                                                    <Typography variant="subtitle1" fontWeight={500} mb={1}>
                                                        Variants
                                                    </Typography>
                                                    <Stack spacing={1}>
                                                        <TextField
                                                            size="small"
                                                            label="Enter Variant Name"
                                                            value={variantInput}
                                                            onChange={(e) => setVariantInput(e.target.value)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    e.preventDefault();
                                                                    handleAddVariant();
                                                                }
                                                            }}
                                                            fullWidth
                                                        />

                                                        {/* Chip Display */}
                                                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ minHeight: '32px' }}>
                                                            {watchedVariants.map((variant, index) => (
                                                                <Chip
                                                                    key={index}
                                                                    label={variant.value}
                                                                    onDelete={() => handleRemoveVariant(index)}
                                                                    color="info"
                                                                    variant="filled"
                                                                    size="small"
                                                                    sx={{
                                                                        mt: 1,
                                                                        '& .MuiChip-label': {
                                                                            fontSize: '0.75rem',
                                                                        },
                                                                    }}
                                                                />
                                                            ))}
                                                        </Stack>
                                                    </Stack>
                                                </Box>

                                                {/* Fuel Types Section */}
                                                <Box>
                                                    <Typography variant="subtitle1" fontWeight={500} mb={1}>
                                                        Fuel Types
                                                    </Typography>
                                                    <Stack direction="row" spacing={1} flexWrap="wrap">
                                                        {FUEL_OPTIONS.map((fuel) => (
                                                            <Chip
                                                                key={fuel}
                                                                label={fuel}
                                                                clickable
                                                                size="small"
                                                                color={fuelType.includes(fuel) ? "success" : "default"}
                                                                variant={fuelType.includes(fuel) ? "filled" : "outlined"}
                                                                onClick={() => handleFuelClick(fuel)}
                                                                sx={{ mb: 1 }}
                                                            />
                                                        ))}
                                                    </Stack>
                                                </Box>

                                                {/* Transmission Types Section */}
                                                <Box>
                                                    <Typography variant="subtitle1" fontWeight={500} mb={1}>
                                                        Transmission Types
                                                    </Typography>
                                                    <Stack direction="row" spacing={1} flexWrap="wrap">
                                                        {TRANSMISSION_OPTIONS.map((type) => (
                                                            <Chip
                                                                key={type}
                                                                label={type}
                                                                clickable
                                                                size="small"
                                                                color={transmission.includes(type) ? "info" : "default"}
                                                                variant={transmission.includes(type) ? "filled" : "outlined"}
                                                                onClick={() => handleTransmissionClick(type)}
                                                                sx={{ mb: 1 }}
                                                            />
                                                        ))}
                                                    </Stack>
                                                </Box>
                                            </Stack>
                                        </Box>
                                    </Stack>
                                </Paper>

                                {/* 2. GENERATIONS */}
                                <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
                                    <Typography variant="h6" fontWeight={600} mb={2}>
                                        Model Generations
                                    </Typography>
                                    <Stack spacing={3}>
                                        {genFields.map((field, index) => (
                                            <Card key={field.id} variant="elevation" elevation={2} sx={{ p: 2, bgcolor: 'grey.50' }}>
                                                <Stack spacing={2}>
                                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                        <Typography variant="subtitle1" fontWeight={600} color="primary.main">
                                                            Generation {index + 1}
                                                        </Typography>
                                                        <IconButton color="error" onClick={() => removeGen(index)} size="small" disabled={genFields.length === 1}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Stack>

                                                    {/* Year Selection & Current Checkbox */}
                                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                                                        <DatePicker
                                                            views={["year"]}
                                                            label="From (Year)"
                                                            slotProps={{
                                                                textField: {
                                                                    size: 'small',
                                                                    fullWidth: true
                                                                }
                                                            }}
                                                            value={watch(`generations.${index}.from`) ? dayjs(watch(`generations.${index}.from`)) : null}
                                                            onChange={(newValue) =>
                                                                setValue(`generations.${index}.from`, newValue ? dayjs(newValue).year().toString() : "")
                                                            }
                                                        />

                                                        <DatePicker
                                                            views={["year"]}
                                                            label="To (Year)"
                                                            slotProps={{
                                                                textField: {
                                                                    size: 'small',
                                                                    fullWidth: true
                                                                }
                                                            }}
                                                            disabled={watch(`generations.${index}.current`)}
                                                            value={watch(`generations.${index}.to`) ? dayjs(watch(`generations.${index}.to`)) : null}
                                                            onChange={(newValue) =>
                                                                setValue(`generations.${index}.to`, newValue ? dayjs(newValue).year().toString() : "")
                                                            }
                                                        />

                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={watch(`generations.${index}.current`)}
                                                                    onChange={(e) =>
                                                                        setValue(`generations.${index}.current`, e.target.checked)
                                                                    }
                                                                />
                                                            }
                                                            label="Current"
                                                            sx={{ minWidth: 100, flexShrink: 0 }}
                                                        />
                                                    </Stack>

                                                    {/* Image Upload for Generation */}
                                                    <Box>
                                                        <Button
                                                            variant="contained"
                                                            component="label"
                                                            startIcon={<CloudUploadIcon />}
                                                            size="small"
                                                            sx={{ mb: 1 }}
                                                            disabled={compressingGenerations[index]}
                                                        >
                                                            {compressingGenerations[index] ? 'Compressing...' : `Upload Images (${watch(`generations.${index}.images`)?.length || 0})`}
                                                            <input
                                                                type="file"
                                                                multiple
                                                                accept="image/*"
                                                                hidden
                                                                onChange={async (e) => {
                                                                    const files = Array.from(e.target.files || []);
                                                                    if (files.length > 0) {
                                                                        await handleGenerationImageUpload(index, files);
                                                                        e.target.value = ''; // Reset input
                                                                    }
                                                                }}
                                                            />
                                                        </Button>

                                                        {compressingGenerations[index] && (
                                                            <LinearProgress sx={{ mb: 1, width: '100%' }} />
                                                        )}

                                                        <Stack direction="row" spacing={1} flexWrap="wrap">
                                                            {watch(`generations.${index}.images`)?.map((img, i) => (
                                                                <Box key={i} sx={{ position: 'relative' }}>
                                                                    <Avatar
                                                                        src={img instanceof File ? URL.createObjectURL(img) : img}
                                                                        variant="rounded"
                                                                        sx={{
                                                                            width: 60,
                                                                            height: 40,
                                                                            bgcolor: 'grey.200',
                                                                            border: baseImage?.generationIndex === index && baseImage?.imageIndex === i ? '2px solid #1976d2' : 'none',
                                                                            '& img': {
                                                                                objectFit: 'cover',
                                                                            }
                                                                        }}
                                                                    />
                                                                    <IconButton
                                                                        size="small"
                                                                        sx={{
                                                                            position: 'absolute',
                                                                            top: -8,
                                                                            right: -8,
                                                                            backgroundColor: 'error.main',
                                                                            color: 'white',
                                                                            width: 20,
                                                                            height: 20,
                                                                            '&:hover': {
                                                                                backgroundColor: 'error.dark',
                                                                            }
                                                                        }}
                                                                        onClick={() => handleRemoveGenerationImage(index, i)}
                                                                    >
                                                                        <DeleteIcon sx={{ fontSize: 14 }} />
                                                                    </IconButton>
                                                                </Box>
                                                            ))}
                                                        </Stack>
                                                    </Box>
                                                </Stack>
                                            </Card>
                                        ))}
                                        <Button
                                            startIcon={<AddCircleOutlineIcon />}
                                            variant="outlined"
                                            size="small"
                                            onClick={() =>
                                                appendGen({ from: "", to: "", current: false, images: [] })
                                            }
                                            sx={{ alignSelf: 'flex-start' }}
                                        >
                                            Add Generation
                                        </Button>
                                    </Stack>
                                </Paper>

                                {/* 3. ACTION BUTTONS */}
                                <Stack direction="row" justifyContent="flex-end" spacing={2} pt={2}>
                                    <Button variant="contained" color="secondary" size="small" onClick={() => reset()}>
                                        Reset Form
                                    </Button>
                                    <Button type="submit" variant="contained" color="primary" size="small" disabled={isCompressing}>
                                        {isCompressing ? 'Compressing...' : 'Save Car Model'}
                                    </Button>
                                </Stack>
                            </Stack>
                        </Box>
                    </CardContent>
                </Card>
            </LocalizationProvider>
        </>
    );
};

export default AddCar;